import { ICONS } from "./icons";
import { VERBS } from "./verbs";
import { EYECON_SIZE } from "./constants";
import { Verb } from "./types";
import { CHESS_URL, GAMES_LAUNCHER_URL } from "../globals";
import {
	calculateArrowRotation,
	sceneComponentsDefaultArrowConfig as configArrow,
} from "../configHelpers";
import { FOCUSABLES } from "./focusables";
import { STATES } from "../states/entities";
import { fullscreenInteractiveNoIcon } from "./utils";
const { NAVIGATE, TOGGLE_RADIO, PLAY_SOUND, CHANGE_STATION, SET_VOLUME, CHANGE_SCENE_STATE } =
	VERBS;

type RequiredInteractives<T> = {
	[K in keyof T]: {
		id: T[K];
		verbs: Verb[];
		src: string;
		protected: boolean;
		position: any;
		disabled?: boolean;
	};
};

export const SPLASH_SCREEN_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.SPLASH_SCREEN> = {
	ENTER_CABIN: {
		id: FOCUSABLES.SPLASH_SCREEN.ENTER_CABIN,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
		src: ICONS.EYECON,
		protected: false,
		position: {
			hitbox: { x: 55, y: 50, width: 30, height: 30 },
			icon: { x: 60, y: 67, ...EYECON_SIZE },
		},
	},
};

export const CABIN_HUB_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABIN_HUB> = {
	NAV_LEFT: configArrow({
		id: FOCUSABLES.CABIN_HUB.NAV_LEFT,
		direction: "left",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-exit" } }],
	}),
	NAV_RIGHT: configArrow({
		id: FOCUSABLES.CABIN_HUB.NAV_RIGHT,
		direction: "right",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-exit" } }],
	}),
	RADIO: {
		id: FOCUSABLES.CABIN_HUB.RADIO,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/radio" } }],
		protected: false,
		src: ICONS.EYECON,
		position: {
			hitbox: { x: 18, y: 45, width: 10, height: 10 },
			icon: {
				x: 21,
				y: 49,
				...EYECON_SIZE,
			},
		},
	},
	GAME_TABLE: {
		id: FOCUSABLES.CABIN_HUB.GAME_TABLE,
		src: ICONS.EYECON,
		protected: false,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/game-table" } }],
		position: {
			hitbox: { x: 50, y: 65, width: 15, height: 15 },
			icon: {
				x: 55,
				y: 73,
				...EYECON_SIZE,
			},
		},
	},
	FIREPLACE: {
		id: FOCUSABLES.CABIN_HUB.FIREPLACE,
		src: ICONS.EYECON,
		protected: false,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/fireplace" } }],
		position: {
			hitbox: { x: 18, y: 55, width: 10, height: 45 },
			icon: {
				x: 20,
				y: 59,
				...EYECON_SIZE,
			},
		},
	},
	TV: {
		id: FOCUSABLES.CABIN_HUB.TV,
		src: ICONS.EYECON,
		protected: false,
		verbs: [
			{
				name: CHANGE_SCENE_STATE,
				data: {
					stateKey: "cabin-tv",
					type: "set",
					options: [STATES.TV.ON],
				},
			},
			{
				name: NAVIGATE,
				data: {
					url: `${GAMES_LAUNCHER_URL}?returnUrl=${window.location.origin}/cabin-hub&remoteEnv=int`,
					delay: 1000,
				},
			},
		],
		position: {
			hitbox: { x: 18, y: 25, width: 10, height: 17 },
			icon: {
				x: 19,
				y: 30,
				...EYECON_SIZE,
			},
		},
	},
	OWL: {
		id: FOCUSABLES.CABIN_HUB.OWL,
		src: ICONS.EYECON,
		protected: false,
		verbs: [
			{
				name: CHANGE_SCENE_STATE,
				data: {
					stateKey: "cabin-fireplace",
					type: "toggle",
					options: [STATES.FIREPLACE.ON, STATES.FIREPLACE.OFF],
				},
			},
		],
		position: {
			hitbox: { x: 28, y: 22, width: 8, height: 10 },
			icon: {
				x: 28,
				y: 25,
				...EYECON_SIZE,
			},
		},
	},
	LIBRARY: {
		id: FOCUSABLES.CABIN_HUB.LIBRARY,
		src: ICONS.EYECON,
		protected: false,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/library" } }],
		position: {
			hitbox: { x: 28, y: 32, width: 10, height: 40 },
			icon: {
				x: 31,
				y: 45,
				...EYECON_SIZE,
			},
		},
	},
	CAT: {
		id: FOCUSABLES.CABIN_HUB.CAT,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/cat" } }],
		protected: false,
		position: {
			hitbox: { x: 28, y: 72, width: 13, height: 28 },
			icon: {
				x: 30,
				y: 82,
				...EYECON_SIZE,
			},
		},
	},
	WINDOW: {
		id: FOCUSABLES.CABIN_HUB.WINDOW,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/window" } }],
		protected: false,
		position: {
			hitbox: { x: 45, y: 33, width: 22, height: 25 },
			icon: {
				x: 57,
				y: 40,
				...EYECON_SIZE,
			},
		},
	},
	COUCH: {
		id: FOCUSABLES.CABIN_HUB.COUCH,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/couch" } }],
		protected: false,
		position: {
			hitbox: { x: 75, y: 72, width: 25, height: 28 },
			icon: {
				x: 83,
				y: 72,
				...EYECON_SIZE,
			},
		},
	},
	DESK: {
		id: FOCUSABLES.CABIN_HUB.DESK,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/desk" } }],
		protected: false,
		position: {
			hitbox: { x: 67, y: 40, width: 25, height: 28 },
			icon: {
				x: 75,
				y: 55,
				...EYECON_SIZE,
			},
		},
	},
};

export const CABIN_COUCH_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABIN_COUCH> = {
	ARROW_LEFT: configArrow({
		id: FOCUSABLES.CABIN_COUCH.ARROW_LEFT,
		direction: "left",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	ARROW_RIGHT: configArrow({
		id: FOCUSABLES.CABIN_COUCH.ARROW_RIGHT,
		direction: "right",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	FIREPLACE: {
		id: FOCUSABLES.CABIN_COUCH.FIREPLACE,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/fireplace" } }],
		protected: false,
		position: {
			hitbox: {
				x: 34,
				y: 55,
				height: 30,
				width: 30,
			},
			icon: { x: 45, y: 65, ...EYECON_SIZE },
		},
	},
	OWL: {
		id: FOCUSABLES.CABIN_COUCH.OWL,
		src: ICONS.EYECON,
		verbs: [
			{
				name: CHANGE_SCENE_STATE,
				data: {
					stateKey: "cabin-fireplace",
					type: "toggle",
					options: [STATES.FIREPLACE.ON, STATES.FIREPLACE.OFF],
				},
			},
		],
		protected: false,
		position: {
			hitbox: {
				x: 63,
				y: 0,
				height: 20,
				width: 30,
			},
			icon: { x: 70, y: 5, ...EYECON_SIZE },
		},
	},
	TV: {
		id: FOCUSABLES.CABIN_COUCH.TV,
		src: ICONS.EYECON,
		protected: false,
		verbs: [
			{
				name: CHANGE_SCENE_STATE,
				data: {
					stateKey: "cabin-tv",
					type: "set",
					options: [STATES.TV.ON],
				},
			},
			{
				name: NAVIGATE,
				data: {
					url: `${GAMES_LAUNCHER_URL}?returnUrl=${window.location.origin}/cabin-couch&remoteEnv=int`,
					delay: 1000,
				},
			},
		],
		position: {
			hitbox: { x: 37, y: 17, width: 25, height: 21 },
			icon: {
				x: 45.5,
				y: 22,
				...EYECON_SIZE,
			},
		},
	},
	RADIO: {
		id: FOCUSABLES.CABIN_COUCH.RADIO,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/radio" } }],
		protected: false,
		position: {
			hitbox: {
				x: 42,
				y: 36,
				height: 15,
				width: 16,
			},
			icon: {
				x: 46,
				y: 44,
				...EYECON_SIZE,
			},
		},
	},
	BOOKSHELF: {
		id: FOCUSABLES.CABIN_COUCH.BOOKSHELF,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/library" } }],
		protected: false,
		position: {
			hitbox: {
				x: 67,
				y: 16,
				height: 75,
				width: 23,
			},
			icon: {
				x: 75,
				y: 45,
				...EYECON_SIZE,
			},
		},
	},
	TABLE: {
		id: FOCUSABLES.CABIN_COUCH.TABLE,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/game-table" } }],
		protected: false,
		position: {
			hitbox: {
				x: 0,
				y: 85,
				height: 15,
				width: 80,
			},
			icon: {
				x: 44,
				y: 87,
				...EYECON_SIZE,
			},
		},
	},
};

export const CABIN_RADIO_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABIN_RADIO> = {
	LEFT_ARROW: configArrow({
		id: FOCUSABLES.CABIN_RADIO.LEFT_ARROW,
		direction: "left",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	RIGHT_ARROW: configArrow({
		id: FOCUSABLES.CABIN_RADIO.RIGHT_ARROW,
		direction: "right",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/library" } }],
	}),
	TOGGLE: {
		id: FOCUSABLES.CABIN_RADIO.TOGGLE,
		src: ICONS.RADIO.POWER,
		protected: false,
		verbs: [{ name: TOGGLE_RADIO, data: "CABIN" }],
		position: {
			hitbox: { x: 40, y: 57, width: 5, height: 10 },
			icon: { x: 39, y: 61, ...EYECON_SIZE },
		},
	},
	STATION_CHANGE: {
		id: FOCUSABLES.CABIN_RADIO.STATION_CHANGE,
		src: ICONS.RADIO.STATION,
		protected: false,
		verbs: [{ name: CHANGE_STATION, data: "CABIN" }],
		position: {
			hitbox: { x: 45, y: 57, width: 5, height: 10 },
			icon: { x: 44, y: 60, ...EYECON_SIZE },
		},
	},
	VOLUME_UP: {
		id: FOCUSABLES.CABIN_RADIO.VOLUME_UP,
		protected: false,
		src: ICONS.ARROW,
		verbs: [{ name: SET_VOLUME, data: { radio: "CABIN", direction: "up" } }],
		position: {
			hitbox: { x: 50, y: 50, width: 5, height: 10 },
			icon: { x: 50, y: 54, width: 5, height: 5 },
		},
	},
	VOLUME_DOWN: {
		id: FOCUSABLES.CABIN_RADIO.VOLUME_DOWN,
		protected: false,
		src: ICONS.ARROW,
		verbs: [{ name: SET_VOLUME, data: { radio: "CABIN", direction: "down" } }],
		position: {
			hitbox: { x: 50, y: 60, width: 5, height: 10 },
			icon: { x: 50, y: 62, width: 5, height: 5, rotation: calculateArrowRotation("down") },
		},
	},
	// Potential future: interactive volume knob center
	// Leaving this here as reference for hitbox/icon data if we implement it    // VOLUME: {
	//     id: FOCUSABLES.CABIN_RADIO.VOLUME,
	//     src: ICONS.EYECON,
	//     protected: false,
	//     verbs: [{ name: CHANGE_STATION, data: "CABIN" }],
	//     position: {
	//         hitbox: { x: 50, y: 57, width: 5, height: 10 },
	//         icon: { x: 49, y: 59, ...EYECON_SIZE },
	//     },
	// },
};

export const CABIN_GAME_TABLE_INTERACTIVES: RequiredInteractives<
	typeof FOCUSABLES.CABIN_GAME_TABLE
> = {
	ARROW_LEFT: configArrow({
		id: FOCUSABLES.CABIN_GAME_TABLE.ARROW_LEFT,
		direction: "left",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	ARROW_RIGHT: configArrow({
		id: FOCUSABLES.CABIN_GAME_TABLE.ARROW_RIGHT,
		direction: "right",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	CHESS: {
		id: FOCUSABLES.CABIN_GAME_TABLE.CHESS,
		src: ICONS.EYECON,
		protected: false,
		verbs: [
			{
				name: NAVIGATE,
				data: {
					url: `${CHESS_URL}?returnUrl=${window.location.origin}/cabin-hub/game-table&remoteEnv=int`,
				},
			},
		],
		position: {
			hitbox: { x: 23, y: 10, width: 55, height: 80 },
			icon: {
				x: 47,
				y: 50,
				...EYECON_SIZE,
			},
		},
	},
};

export const CABIN_LIBRARY_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABIN_LIBRARY> = {
	ARROW_LEFT: configArrow({
		id: FOCUSABLES.CABIN_LIBRARY.ARROW_LEFT,
		direction: "left",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	ARROW_RIGHT: configArrow({
		id: FOCUSABLES.CABIN_LIBRARY.ARROW_RIGHT,
		direction: "right",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	FIREPLACE: {
		id: FOCUSABLES.CABIN_LIBRARY.FIREPLACE,
		src: ICONS.EYECON,
		protected: false,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/fireplace" } }],
		position: {
			hitbox: {
				x: 10,
				y: 60,
				height: 35,
				width: 20,
			},
			icon: {
				x: 15,
				y: 65,
				...EYECON_SIZE,
			},
		},
	},
	RADIO: {
		id: FOCUSABLES.CABIN_LIBRARY.RADIO,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/radio" } }],
		protected: false,
		position: {
			hitbox: {
				x: 10,
				y: 35,
				height: 15,
				width: 15,
			},
			icon: {
				x: 13,
				y: 40,
				...EYECON_SIZE,
			},
		},
	},
	BOOKSHELF: {
		id: FOCUSABLES.CABIN_LIBRARY.BOOKSHELF,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "" } }],
		protected: false,
		position: {
			hitbox: {
				x: 40,
				y: 9,
				height: 90,
				width: 28,
			},
			icon: {
				x: 50,
				y: 45,
				...EYECON_SIZE,
			},
		},
	},
};

export const CABIN_DESK_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABIN_DESK> = {
	ARROW_LEFT: configArrow({
		id: FOCUSABLES.CABIN_DESK.ARROW_LEFT,
		direction: "left",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	ARROW_RIGHT: configArrow({
		id: FOCUSABLES.CABIN_DESK.ARROW_RIGHT,
		direction: "right",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	CARD_CRAFT: {
		id: FOCUSABLES.CABIN_DESK.CARD_CRAFT,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "" } }],
		protected: false,
		disabled: true, // Using the new disabled property instead of commenting out
		position: {
			hitbox: { x: 18, y: 40, width: 64, height: 30 },
			icon: {
				x: 44,
				y: 50,
				...EYECON_SIZE,
			},
		},
	},
	DRAWER: {
		id: FOCUSABLES.CABIN_DESK.DRAWER,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/desk/drawer" } }],
		protected: false,
		position: {
			hitbox: { x: 15, y: 70, width: 25, height: 30 },
			icon: {
				x: 23,
				y: 80,
				...EYECON_SIZE,
			},
		},
	},
};

export const CABIN_DESK_DRAWER_INTERACTIVES: RequiredInteractives<
	typeof FOCUSABLES.CABIN_DESK_DRAWER
> = {
	ARROW_LEFT: configArrow({
		id: FOCUSABLES.CABIN_DESK_DRAWER.ARROW_LEFT,
		direction: "left",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	ARROW_RIGHT: configArrow({
		id: FOCUSABLES.CABIN_DESK_DRAWER.ARROW_RIGHT,
		direction: "right",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	TICKETS: {
		id: FOCUSABLES.CABIN_DESK_DRAWER.TICKETS,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "/plane" } }],
		protected: false,
		position: {
			hitbox: { x: 18, y: 40, width: 64, height: 30 },
			icon: {
				x: 44,
				y: 50,
				...EYECON_SIZE,
			},
		},
	},
};

export const CABIN_EXIT_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABIN_EXIT> = {
	ARROW_LEFT: configArrow({
		id: FOCUSABLES.CABIN_EXIT.ARROW_LEFT,
		direction: "left",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	ARROW_RIGHT: configArrow({
		id: FOCUSABLES.CABIN_EXIT.ARROW_RIGHT,
		direction: "right",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	LAMP: {
		id: FOCUSABLES.CABIN_EXIT.LAMP,
		src: ICONS.EYECON,
		verbs: [{ name: NAVIGATE, data: { url: "" } }],
		protected: false,
		disabled: true,
		position: {
			hitbox: { x: 5, y: 50, width: 20, height: 25 },
			icon: {
				x: 10,
				y: 54,
				...EYECON_SIZE,
			},
		},
	},
	EXIT: {
		id: FOCUSABLES.CABIN_EXIT.EXIT,
		src: ICONS.EYECON,
		protected: false,
		verbs: [
			{ name: NAVIGATE, data: { url: "/" } },
			{ name: PLAY_SOUND, data: { playlist: "DOOR_OPEN", gain: 0.5 } },
		],
		position: {
			hitbox: { x: 18, y: 40, width: 64, height: 30 },
			icon: {
				x: 44,
				y: 50,
				...EYECON_SIZE,
			},
		},
	},
};

export const CABANA_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABANA> = {
	ARROW_UP: configArrow({
		id: FOCUSABLES.CABANA.ARROW_UP,
		direction: "up",
		verbs: [{ name: NAVIGATE, data: { url: "/cabana/beach" } }],
		hitbox: {
			x: 22.5,
			y: 22.5,
			width: 50,
			height: 75,
		},
	}),
	PLANE_RETURN: {
		id: FOCUSABLES.CABANA.PLANE_RETURN,
		src: ICONS.EYECON,
		protected: false,
		verbs: [{ name: NAVIGATE, data: { url: "/plane-return" } }],
		position: {
			hitbox: { x: 65, y: 75, width: 10, height: 20 },
			icon: {
				x: 65,
				y: 80,
				...EYECON_SIZE,
			},
		},
	},
};

export const CABIN_FIREPLACE_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABIN_FIREPLACE> =
	{
		ARROW_LEFT: configArrow({
			id: FOCUSABLES.CABIN_FIREPLACE.ARROW_LEFT,
			direction: "left",
			verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
		}),
		ARROW_RIGHT: configArrow({
			id: FOCUSABLES.CABIN_FIREPLACE.ARROW_RIGHT,
			direction: "right",
			verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
		}),
	};

export const CABIN_WINDOW_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABIN_WINDOW> = {
	ARROW_CENTER: configArrow({
		id: FOCUSABLES.CABIN_WINDOW.ARROW_CENTER,
		direction: "down",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
};

export const CABANA_BEACH_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABANA_BEACH> = {
	ARROW_DOWN: configArrow({
		id: FOCUSABLES.CABANA_BEACH.ARROW_DOWN,
		verbs: [{ name: NAVIGATE, data: { url: "/cabana" } }],
		direction: "down",
		hitbox: {
			x: 25,
			y: 60,
			width: 50,
			height: 40,
		},
	}),
};

export const CABIN_KITCHEN_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABIN_KITCHEN> = {
	ARROW_LEFT: configArrow({
		id: FOCUSABLES.CABIN_KITCHEN.ARROW_LEFT,
		direction: "left",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	ARROW_RIGHT: configArrow({
		id: FOCUSABLES.CABIN_KITCHEN.ARROW_RIGHT,
		direction: "right",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-exit" } }],
	}),
};

export const CABIN_CAT_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.CABIN_CAT> = {
	PET: {
		id: FOCUSABLES.CABIN_CAT.PET,
		src: ICONS.EYECON,
		protected: false,
		verbs: [
			{
				name: "CHANGE_SCENE_STATE",
				data: {
					stateKey: "cabin-cat",
					type: "random",
					options: [STATES.CABIN_CAT.TWITCH, STATES.CABIN_CAT.YAWN],
				},
			},
			{
				name: NAVIGATE,
				data: {
					url: "/cabin-hub/cat/interactive",
				},
			},
		],
		position: {
			hitbox: { x: 18, y: 40, width: 64, height: 30 },
			icon: {
				x: 44,
				y: 50,
				...EYECON_SIZE,
			},
		},
	},
	NAV_LEFT: configArrow({
		id: FOCUSABLES.CABIN_CAT.NAV_LEFT,
		direction: "left",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
	NAV_RIGHT: configArrow({
		id: FOCUSABLES.CABIN_CAT.NAV_RIGHT,
		direction: "right",
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	}),
};

export const PLANE_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.PLANE> = {
	SKIP_TO_CABANA: {
		...fullscreenInteractiveNoIcon,
		id: FOCUSABLES.PLANE.SKIP_TO_CABANA,
		verbs: [{ name: NAVIGATE, data: { url: "/cabana" } }],
	},
};

export const PLANE_RETURN_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.PLANE_RETURN> = {
	SKIP_TO_CABIN: {
		...fullscreenInteractiveNoIcon,
		id: FOCUSABLES.PLANE_RETURN.SKIP_TO_CABIN,
		verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub" } }],
	},
};

export const INTERACTIVE_CAT_INTERACTIVES: RequiredInteractives<typeof FOCUSABLES.INTERACTIVE_CAT> =
	{
		SKIP: {
			...fullscreenInteractiveNoIcon,
			id: FOCUSABLES.INTERACTIVE_CAT.SKIP,
			verbs: [{ name: NAVIGATE, data: { url: "/cabin-hub/cat" } }],
		},
	};
